import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Plus, 
  Pencil, 
  Trash,
  ArrowLeft,
  VideoCamera,
  FileText,
  Brain,
  Spinner,
  UploadSimple,
  CloudArrowUp,
  CheckCircle,
  LinkSimple,
  DownloadSimple,
  FileXls
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTENT_TYPE_LABELS = {
  video: 'Video',
  pdf: 'Documento PDF',
  quiz: 'Questionario',
};

function FileUploadZone({ accept, label, fileType, currentUrl, onUpload, onUrlChange }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [useUrl, setUseUrl] = useState(!!currentUrl && !currentUrl.includes('/api/files/'));
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/api/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / e.total);
          setProgress(pct);
        }
      });
      setUploadedFile({ name: file.name, size: file.size, url: response.data.file_url });
      onUpload(response.data.file_url);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao fazer upload';
      alert(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <Label className="text-[#1A252F] font-semibold text-sm">{label}</Label>
        {fileType === 'video' && (
          <button
            type="button"
            onClick={() => { setUseUrl(!useUrl); setUploadedFile(null); }}
            className="text-xs text-[#27AE60] hover:underline flex items-center gap-1"
          >
            <LinkSimple size={12} />
            {useUrl ? 'Fazer upload' : 'Usar URL (YouTube/Drive)'}
          </button>
        )}
      </div>

      {useUrl ? (
        <Input
          value={currentUrl || ''}
          onChange={(e) => onUrlChange(e.target.value)}
          placeholder="Cole o link do YouTube ou Google Drive"
          className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F] placeholder:text-[#95A5A6]"
          data-testid={`module-${fileType}-url`}
        />
      ) : (
        <>
          {uploadedFile || (currentUrl && currentUrl.includes('/api/files/')) ? (
            <div className="flex items-center gap-3 p-3 bg-[#E8F5E9] rounded-lg border border-[#27AE60]/30">
              <CheckCircle size={20} weight="fill" className="text-[#27AE60] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1A252F] truncate">
                  {uploadedFile?.name || 'Arquivo enviado'}
                </p>
                {uploadedFile?.size && (
                  <p className="text-xs text-[#5C6B7A]">{formatSize(uploadedFile.size)}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setUploadedFile(null); onUpload(''); }}
                className="text-[#E74C3C] hover:text-[#C0392B] text-xs font-medium"
              >
                Remover
              </button>
            </div>
          ) : (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer
                ${dragOver ? 'border-[#27AE60] bg-[#27AE60]/5' : 'border-[#BDC3C7] hover:border-[#27AE60]/50 bg-[#F8F9FA]'}
                ${uploading ? 'pointer-events-none opacity-70' : ''}
              `}
              onClick={() => !uploading && document.getElementById(`file-input-${fileType}`)?.click()}
              data-testid={`upload-zone-${fileType}`}
            >
              <input
                id={`file-input-${fileType}`}
                type="file"
                accept={accept}
                className="hidden"
                onChange={(e) => handleFile(e.target.files[0])}
              />
              {uploading ? (
                <div className="space-y-2">
                  <Spinner size={28} className="animate-spin text-[#27AE60] mx-auto" />
                  <p className="text-sm text-[#5C6B7A]">Enviando... {progress}%</p>
                  <div className="w-48 mx-auto h-2 bg-[#E8ECF0] rounded-full overflow-hidden">
                    <div className="h-full bg-[#27AE60] rounded-full transition-all" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              ) : (
                <>
                  <CloudArrowUp size={32} className="mx-auto text-[#95A5A6] mb-2" />
                  <p className="text-sm text-[#5C6B7A]">
                    Arraste o arquivo aqui ou <span className="text-[#27AE60] font-medium">clique para selecionar</span>
                  </p>
                  <p className="text-xs text-[#95A5A6] mt-1">
                    {fileType === 'video' ? 'MP4, WebM (max 200MB)' : 'PDF (max 50MB)'}
                  </p>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function AdminModules() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [formData, setFormData] = useState({
    course_id: courseId,
    title: '',
    description: '',
    order: 1,
    estimated_duration: 15,
    content_type: 'video',
    video_url: '',
    pdf_url: '',
    quiz_data: null
  });
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [uploadingExcel, setUploadingExcel] = useState(false);

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/courses/${courseId}`, { withCredentials: true });
      setCourse(response.data);
    } catch (error) {
      console.error('Error fetching course:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = { ...formData };
    if (formData.content_type === 'quiz') {
      submitData.quiz_data = {
        time_limit: parseInt(document.getElementById('time_limit')?.value) || 600,
        passing_score: parseInt(document.getElementById('passing_score')?.value) || 70,
        randomize: true,
        questions: quizQuestions
      };
    }
    try {
      if (editingModule) {
        await axios.put(`${API_URL}/api/modules/${editingModule.id}`, submitData, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/modules`, submitData, { withCredentials: true });
      }
      setDialogOpen(false);
      setEditingModule(null);
      resetForm();
      fetchCourse();
    } catch (error) {
      console.error('Error saving module:', error);
    }
  };

  const handleEdit = (module) => {
    setEditingModule(module);
    setFormData({
      course_id: courseId,
      title: module.title,
      description: module.description,
      order: module.order,
      estimated_duration: module.estimated_duration || 15,
      content_type: module.content_type,
      video_url: module.video_url || '',
      pdf_url: module.pdf_url || '',
      quiz_data: module.quiz_data
    });
    if (module.quiz_data?.questions) {
      setQuizQuestions(module.quiz_data.questions);
    }
    setDialogOpen(true);
  };

  const handleDelete = async (moduleId) => {
    if (!window.confirm('Tem certeza que deseja excluir este modulo?')) return;
    try {
      await axios.delete(`${API_URL}/api/modules/${moduleId}`, { withCredentials: true });
      fetchCourse();
    } catch (error) {
      console.error('Error deleting module:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      course_id: courseId,
      title: '',
      description: '',
      order: (course?.modules?.length || 0) + 1,
      estimated_duration: 15,
      content_type: 'video',
      video_url: '',
      pdf_url: '',
      quiz_data: null
    });
    setQuizQuestions([]);
  };

  const addQuestion = () => {
    setQuizQuestions([
      ...quizQuestions,
      {
        id: `q${quizQuestions.length + 1}`,
        type: 'multiple_choice',
        question: '',
        options: ['', '', '', ''],
        correct_answer: ''
      }
    ]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...quizQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setQuizQuestions(updated);
  };

  const removeQuestion = (index) => {
    setQuizQuestions(quizQuestions.filter((_, i) => i !== index));
  };

  const handleExcelUpload = async (file) => {
    if (!file) return;
    setUploadingExcel(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/api/quiz/parse-excel`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setQuizQuestions(response.data.questions);
      alert(`${response.data.count} perguntas importadas com sucesso!`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao importar planilha');
    } finally {
      setUploadingExcel(false);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'video': return VideoCamera;
      case 'pdf': return FileText;
      case 'quiz': return Brain;
      default: return FileText;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20">
          <Spinner size={48} className="animate-spin text-[#2C3E50]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="admin-modules">
        <Link to="/admin/courses" className="inline-flex items-center gap-2 text-[#5C6B7A] hover:text-[#2C3E50] mb-6">
          <ArrowLeft size={20} />
          Voltar para Cursos
        </Link>

        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A252F] mb-2">{course?.title}</h1>
            <p className="text-[#5C6B7A]">Gerencie os modulos e conteudo do curso</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingModule(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#27AE60] hover:bg-[#219653]" data-testid="new-module-btn">
                <Plus size={18} className="mr-2" />
                Novo Modulo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-white border-0 shadow-2xl rounded-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
              <div className="relative px-8 pt-7 pb-5 sticky top-0 bg-white z-10">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#27AE60] to-[#2C3E50]" />
                <DialogHeader>
                  <DialogTitle className="text-[#1A252F] text-xl font-bold tracking-tight">
                    {editingModule ? 'Editar Modulo' : 'Criar Novo Modulo'}
                  </DialogTitle>
                  <p className="text-[#5C6B7A] text-sm mt-1">
                    {editingModule ? 'Atualize as informacoes do modulo' : 'Preencha os campos para adicionar um modulo ao curso'}
                  </p>
                </DialogHeader>
              </div>
              <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5" data-testid="module-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title" className="text-[#1A252F] font-semibold text-sm">
                      Titulo do Modulo <span className="text-[#E74C3C]">*</span>
                    </Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Ex: Introducao ao Modo Supervisor"
                      required
                      className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F] placeholder:text-[#95A5A6]"
                      data-testid="module-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="order" className="text-[#1A252F] font-semibold text-sm">
                      Ordem
                    </Label>
                    <Input
                      id="order"
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                      className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F]"
                      data-testid="module-order-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-[#1A252F] font-semibold text-sm">
                    Descricao <span className="text-[#E74C3C]">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descreva o conteudo deste modulo..."
                    required
                    rows={2}
                    className="border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F] placeholder:text-[#95A5A6] resize-none"
                    data-testid="module-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#1A252F] font-semibold text-sm">Tipo de Conteudo</Label>
                    <Select
                      value={formData.content_type}
                      onValueChange={(value) => setFormData({ ...formData, content_type: value })}
                    >
                      <SelectTrigger className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:border-[#27AE60] text-[#1A252F]" data-testid="module-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="video">Video</SelectItem>
                        <SelectItem value="pdf">Documento PDF</SelectItem>
                        <SelectItem value="quiz">Questionario</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="duration" className="text-[#1A252F] font-semibold text-sm">Duracao (minutos)</Label>
                    <Input
                      id="duration"
                      type="number"
                      value={formData.estimated_duration}
                      onChange={(e) => setFormData({ ...formData, estimated_duration: parseInt(e.target.value) })}
                      className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F]"
                      data-testid="module-duration-input"
                    />
                  </div>
                </div>

                {/* Video Upload / URL */}
                {formData.content_type === 'video' && (
                  <FileUploadZone
                    accept="video/mp4,video/webm,video/quicktime"
                    label="Video do Modulo"
                    fileType="video"
                    currentUrl={formData.video_url}
                    onUpload={(url) => setFormData({ ...formData, video_url: url })}
                    onUrlChange={(url) => setFormData({ ...formData, video_url: url })}
                  />
                )}

                {/* PDF Upload */}
                {formData.content_type === 'pdf' && (
                  <FileUploadZone
                    accept="application/pdf"
                    label="Documento PDF"
                    fileType="pdf"
                    currentUrl={formData.pdf_url}
                    onUpload={(url) => setFormData({ ...formData, pdf_url: url })}
                    onUrlChange={(url) => setFormData({ ...formData, pdf_url: url })}
                  />
                )}

                {/* Quiz Builder */}
                {formData.content_type === 'quiz' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="time_limit" className="text-[#1A252F] font-semibold text-sm">Tempo Limite (segundos)</Label>
                        <Input
                          id="time_limit"
                          type="number"
                          defaultValue={formData.quiz_data?.time_limit || 600}
                          className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F]"
                          data-testid="quiz-time-limit"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="passing_score" className="text-[#1A252F] font-semibold text-sm">Nota Minima (%)</Label>
                        <Input
                          id="passing_score"
                          type="number"
                          defaultValue={formData.quiz_data?.passing_score || 70}
                          className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F]"
                          data-testid="quiz-passing-score"
                        />
                      </div>
                    </div>

                    <div className="border-t border-[#E8ECF0] pt-4">
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-3">
                          <Label className="text-[#1A252F] font-semibold text-sm">Perguntas ({quizQuestions.length})</Label>
                          <Button type="button" size="sm" onClick={addQuestion} className="bg-[#2C3E50] hover:bg-[#1A252F] text-white" data-testid="add-question-btn">
                            <Plus size={14} className="mr-1" /> Adicionar Pergunta
                          </Button>
                        </div>
                        
                        {/* Excel import */}
                        <div className="bg-[#F0F7FF] rounded-lg border border-[#2980B9]/20 p-4 mb-4">
                          <p className="text-sm font-medium text-[#1A252F] mb-2">Importar perguntas via planilha</p>
                          <div className="flex flex-wrap gap-2">
                            <a
                              href={`${API_URL}/api/quiz/template`}
                              download
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-[#2980B9]/30 bg-white text-[#2980B9] hover:bg-[#2980B9]/5 transition-colors"
                              data-testid="download-template-btn"
                            >
                              <DownloadSimple size={14} />
                              Baixar Modelo
                            </a>
                            <label className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-[#27AE60]/30 bg-white text-[#27AE60] hover:bg-[#27AE60]/5 transition-colors cursor-pointer ${uploadingExcel ? 'opacity-50 pointer-events-none' : ''}`}>
                              {uploadingExcel ? <Spinner size={14} className="animate-spin" /> : <FileXls size={14} />}
                              {uploadingExcel ? 'Importando...' : 'Enviar Planilha'}
                              <input
                                type="file"
                                accept=".xlsx,.xls"
                                className="hidden"
                                onChange={(e) => handleExcelUpload(e.target.files[0])}
                                data-testid="upload-excel-input"
                              />
                            </label>
                          </div>
                          <p className="text-xs text-[#5C6B7A] mt-2">Baixe o modelo, preencha e envie. As perguntas existentes serao substituidas.</p>
                        </div>
                      </div>
                      
                      {quizQuestions.map((q, index) => (
                        <div key={index} className="bg-[#F8F9FA] rounded-xl border border-[#E8ECF0] p-4 mb-4" data-testid={`question-${index}`}>
                          <div className="flex justify-between items-start mb-3">
                            <Label className="text-[#1A252F] font-semibold text-sm">Pergunta {index + 1}</Label>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeQuestion(index)} className="text-[#E74C3C] hover:text-[#E74C3C] hover:bg-[#E74C3C]/10">
                              <Trash size={16} />
                            </Button>
                          </div>
                          <Input
                            value={q.question}
                            onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                            placeholder="Digite a pergunta"
                            className="mb-3 h-11 border-[#BDC3C7] bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F] placeholder:text-[#95A5A6]"
                          />
                          <Select
                            value={q.type}
                            onValueChange={(value) => updateQuestion(index, 'type', value)}
                          >
                            <SelectTrigger className="mb-3 h-11 border-[#BDC3C7] bg-white text-[#1A252F]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="multiple_choice">Multipla Escolha</SelectItem>
                              <SelectItem value="true_false">Verdadeiro/Falso</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {q.type === 'multiple_choice' && (
                            <div className="space-y-2">
                              {q.options.map((opt, optIndex) => (
                                <Input
                                  key={optIndex}
                                  value={opt}
                                  onChange={(e) => {
                                    const newOptions = [...q.options];
                                    newOptions[optIndex] = e.target.value;
                                    updateQuestion(index, 'options', newOptions);
                                  }}
                                  placeholder={`Opcao ${optIndex + 1}`}
                                  className="h-10 border-[#BDC3C7] bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F] placeholder:text-[#95A5A6]"
                                />
                              ))}
                              <Select
                                value={q.correct_answer}
                                onValueChange={(value) => updateQuestion(index, 'correct_answer', value)}
                              >
                                <SelectTrigger className="h-11 border-[#BDC3C7] bg-white text-[#1A252F]">
                                  <SelectValue placeholder="Selecione a resposta correta" />
                                </SelectTrigger>
                                <SelectContent>
                                  {q.options.filter(o => o).map((opt, i) => (
                                    <SelectItem key={i} value={opt}>{opt}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          
                          {q.type === 'true_false' && (
                            <Select
                              value={String(q.correct_answer)}
                              onValueChange={(value) => updateQuestion(index, 'correct_answer', value === 'true')}
                            >
                              <SelectTrigger className="h-11 border-[#BDC3C7] bg-white text-[#1A252F]">
                                <SelectValue placeholder="Selecione a resposta correta" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="true">Verdadeiro</SelectItem>
                                <SelectItem value="false">Falso</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      ))}

                      {quizQuestions.length === 0 && (
                        <p className="text-sm text-[#5C6B7A] text-center py-4">Nenhuma pergunta adicionada. Clique em "Adicionar Pergunta" acima.</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-2 pb-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1 h-11 border-[#BDC3C7] text-[#5C6B7A] hover:bg-[#F8F9FA] hover:text-[#1A252F] font-medium"
                    onClick={() => setDialogOpen(false)}
                    data-testid="cancel-module-btn"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 h-11 bg-[#27AE60] hover:bg-[#219653] text-white font-medium shadow-md shadow-[#27AE60]/20"
                    data-testid="save-module-btn"
                  >
                    {editingModule ? 'Atualizar Modulo' : 'Criar Modulo'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Modules List */}
        <div className="space-y-3">
          {course?.modules?.sort((a, b) => a.order - b.order).map((module) => {
            const Icon = getIcon(module.content_type);
            return (
              <div 
                key={module.id}
                className="bg-white rounded-xl border border-[#2C3E50]/10 p-4 flex items-center gap-4 hover:shadow-sm transition-shadow"
                data-testid={`module-row-${module.id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#2C3E50] flex items-center justify-center flex-shrink-0">
                  <Icon size={20} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1A252F]">{module.order}. {module.title}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-[#5C6B7A]">
                      {CONTENT_TYPE_LABELS[module.content_type] || module.content_type} &bull; {module.estimated_duration} min
                    </p>
                    {((module.content_type === 'video' && module.video_url) || (module.content_type === 'pdf' && module.pdf_url)) && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#E8F5E9] text-[#27AE60] font-medium">
                        <UploadSimple size={10} /> Arquivo enviado
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(module)} data-testid={`edit-module-${module.id}`}>
                    <Pencil size={18} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(module.id)} className="text-[#E74C3C] hover:text-[#E74C3C] hover:bg-[#E74C3C]/10">
                    <Trash size={18} />
                  </Button>
                </div>
              </div>
            );
          })}
          
          {(!course?.modules || course.modules.length === 0) && (
            <div className="bg-white rounded-xl border border-[#2C3E50]/10 p-12 text-center text-[#5C6B7A]">
              Nenhum modulo ainda. Adicione o primeiro modulo!
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AdminModules;
